import { useState } from 'react';
import { apiClient } from '../api/client';
import type { User } from '../api/types';
import { formatRuPhoneInput, isValidRuPhone, normalizeRuPhone } from '../utils/phone';

interface LoginProps {
  onLogoClick?: () => void;
  onSuccess?: (user: User) => void;
  onCancel?: () => void;
  onRegisterClick?: () => void;
}

export default function Login({
  onLogoClick: _onLogoClick,
  onSuccess,
  onCancel,
  onRegisterClick,
}: LoginProps) {
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatRuPhoneInput(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phone.trim()) {
      setError('Укажите номер телефона');
      return;
    }

    if (!isValidRuPhone(phone)) {
      setError('Введите телефон в формате +7 (999) 999-99-99');
      return;
    }

    setIsLoading(true);

    try {
      const normalizedPhone = normalizeRuPhone(phone);
      const response = await apiClient.getUserByPhone(normalizedPhone);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Пользователь с таким номером телефона не найден');
      }

      onSuccess?.(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при входе');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex-1 bg-[#f3f3f3]">
      <main className="relative mx-auto w-full max-w-[1520px] px-4 pb-[32px] pt-[20px] sm:px-6 lg:px-8">
        <div className="mb-[24px] sm:mb-[32px]">
          <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
            Вход
          </h1>
        </div>

        <div className="mx-auto max-w-[620px]">
          <div className="rounded-[20px] bg-white p-5 shadow-lg sm:p-8 lg:p-10">
            <div className="mb-6 sm:mb-7">
              <p className="text-[16px] leading-[1.5] text-gray-600 sm:text-[18px]">
                Введите номер телефона, который вы использовали при регистрации.
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-[10px] border border-red-200 bg-red-50 p-[15px]">
                <p className="text-[15px] text-red-600 sm:text-[16px]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="phone" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                  Номер телефона <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                  placeholder="+7 (999) 999-99-99"
                  maxLength={18}
                  required
                />
                <p className="mt-2 text-[13px] leading-[1.45] text-gray-500 sm:text-[14px]">
                  Используйте российский номер в формате <code>+7 (999) 999-99-99</code>.
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-[15px]">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 rounded-[12px] bg-[#5d76cb] py-[14px] text-[18px] font-semibold text-white transition-all duration-200 hover:bg-[#4d66bb] disabled:cursor-not-allowed disabled:opacity-50 sm:text-[20px]"
                >
                  {isLoading ? 'Вход...' : 'Войти'}
                </button>
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading}
                    className="rounded-[12px] bg-gray-200 px-[24px] py-[14px] text-[18px] font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-[20px]"
                  >
                    Отмена
                  </button>
                )}
              </div>

              {onRegisterClick && (
                <div className="border-t border-gray-200 pt-[18px] text-center">
                  <p className="mb-[6px] text-[15px] text-gray-600 sm:text-[16px]">Ещё нет аккаунта?</p>
                  <button
                    type="button"
                    onClick={onRegisterClick}
                    className="text-[15px] font-semibold text-[#5d76cb] hover:underline sm:text-[16px]"
                  >
                    Перейти к регистрации
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
